import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatCrowns, formatBigNum } from '../util/formatBig.js';
import { attachItemInfoToggle } from '../components/ItemInfoPanel.js';
import type {
  QuestDto,
  DonationDto,
  CaravanCommissionDto,
  CaravanWareDto,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger';
  message: string;
}

export async function renderDailyTasks(root: HTMLElement): Promise<void> {
  let quests: QuestDto[] = [];
  let donations: DonationDto[] = [];
  let caravanCommissions: CaravanCommissionDto[] = [];
  let caravanWares: CaravanWareDto[] = [];
  let caravanSeals = 0;
  let date = '';
  let flash: FlashState | null = null;

  async function refresh(): Promise<void> {
    try {
      // Also refresh the character so the header's crowns/boons stay live after a claim.
      const me = await api.me();
      store.set({ account: me.account, character: me.character });
      const [q, d, cv] = await Promise.all([
        api.getTodayQuests(),
        api.getTodayDonations(),
        api.getCaravan(),
      ]);
      quests = q.quests;
      date = q.date;
      donations = d.donations;
      caravanCommissions = cv.commissions;
      caravanWares = cv.wares;
      caravanSeals = cv.seals;
    } catch (err) {
      flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed to load tasks.' };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    const renderMain = (): void => {
      const claimedCount = quests.filter((q) => q.claimedAt).length;
      const totalCrowns = quests.reduce((sum, q) => sum + BigInt(q.rewardCrowns), 0n);
      const totalBoons = quests.reduce((sum, q) => sum + q.rewardBoons, 0);
      const totalXp = quests.reduce((sum, q) => sum + q.rewardXp, 0);

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/daily.webp" alt="" />
        <h1>Daily Tasks</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Three charges given each day at dawn. Complete them, claim the reward, repeat tomorrow.
        </p>

        <div id="flash"></div>

        <div class="panel daily-summary">
          <div class="daily-summary-figure">
            <div class="label">Day</div>
            <div class="value">${escapeHtml(date)}</div>
            <div class="cap">${claimedCount}/${quests.length} claimed</div>
          </div>
          <div class="daily-summary-figure">
            <div class="label">Total rewards on offer</div>
            <div class="value gold">${formatCrowns(totalCrowns.toString())} crowns</div>
            <div class="cap">${totalBoons > 0 ? `+ ${totalBoons} boon${totalBoons === 1 ? '' : 's'} · ` : ''}+ ${formatBigNum(totalXp)} XP</div>
          </div>
          <div class="daily-summary-figure">
            <div class="label">Next refresh</div>
            <div class="value">${timeUntilUtcMidnight()}</div>
            <div class="cap">at UTC midnight</div>
          </div>
        </div>

        <div class="panel">
          <div class="task-list">
            ${quests.length === 0
              ? '<p style="font-style:italic; color: var(--c-ink-soft);">No tasks today. Check back after the next refresh.</p>'
              : quests.map(renderTaskRow).join('')}
          </div>
        </div>

        <img class="caravan-banner" src="/img/banners/almshouse.webp" alt="" onerror="this.remove()" />
        <h2 class="alms-head">The Almshouse</h2>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Give up the curios gathering dust in your coffers. The poor are grateful, and so are the Keepers — alms are repaid in crowns, experience, and on the rarest gifts, boons. Click an item's name to see what it is and where it's found.
        </p>
        <div class="panel">
          <div class="task-list">
            ${donations.length === 0
              ? '<p style="font-style:italic; color: var(--c-ink-soft);">The almshouse asks nothing of you today. Return after the next refresh.</p>'
              : donations.map(renderDonationRow).join('')}
          </div>
        </div>

        <img class="caravan-banner" src="/img/banners/caravan.webp" alt="" onerror="this.remove()" />
        <h2 class="alms-head">The Wandering Caravan
          <span class="caravan-seals" title="Caravan Seals — earned from commissions, spent only here.">◈ ${caravanSeals.toLocaleString()} Seals</span>
        </h2>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          The caravan-master pays in Seals for fresh hand-crafted goods — one commission from each craft each day. Fill them from your stores, then spend Seals on the caravan's own wares below. Seals never buy crowns, and the caravan's goods never reach the market.
        </p>
        <div class="panel">
          <div class="task-list">
            ${caravanCommissions.length === 0
              ? '<p style="font-style:italic; color: var(--c-ink-soft);">The road is quiet today. The caravan returns after the next refresh.</p>'
              : caravanCommissions.map(renderCommissionRow).join('')}
          </div>
        </div>

        <div class="panel caravan-shop">
          <h3 class="caravan-shop-head">The Caravan's Wares</h3>
          <div class="caravan-ware-grid">
            ${caravanWares.map((w) => renderWare(w, caravanSeals)).join('')}
          </div>
        </div>
      `;

      const style = document.createElement('style');
      style.textContent = STYLES;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      main.querySelectorAll<HTMLButtonElement>('button[data-claim]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.claim!;
          btn.disabled = true;
          try {
            const r = await api.claimQuest(slug);
            flash = {
              kind: 'success',
              message: `Claimed: ${r.quest.name}. +${formatCrowns(r.paid.crowns)} crowns${r.paid.boons > 0 ? `, +${r.paid.boons} boon${r.paid.boons === 1 ? '' : 's'}` : ''}, +${formatBigNum(r.paid.xp)} XP.`,
            };
            await refresh();
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Claim failed.' };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Almsgiving — donate buttons.
      main.querySelectorAll<HTMLButtonElement>('button[data-donate]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const itemSlug = btn.dataset.donate!;
          btn.disabled = true;
          try {
            const r = await api.fulfilDonation(itemSlug);
            flash = {
              kind: 'success',
              message: `Donated ${r.donation.qty} ${r.donation.itemName}. +${formatCrowns(r.paid.crowns)} crowns${r.paid.boons > 0 ? `, +${r.paid.boons} boon${r.paid.boons === 1 ? '' : 's'}` : ''}, +${formatBigNum(r.paid.xp)} XP. The poor bless thy name.`,
            };
            await refresh();
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Donation failed.' };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Caravan — fulfil commission buttons.
      main.querySelectorAll<HTMLButtonElement>('button[data-commission]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const family = btn.dataset.commission!;
          btn.disabled = true;
          try {
            const r = await api.fulfilCommission(family);
            flash = {
              kind: 'success',
              message: `The caravan takes your ${r.qty}× ${r.itemName}. +${r.sealsEarned} Seals (you now hold ${r.sealsBalance}).`,
            };
            await refresh();
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'The caravan turned it away.' };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Caravan — buy ware buttons.
      main.querySelectorAll<HTMLButtonElement>('button[data-ware]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const wareSlug = btn.dataset.ware!;
          btn.disabled = true;
          try {
            const r = await api.buyCaravanWare(wareSlug);
            flash = {
              kind: 'success',
              message: `${r.granted}. −${r.sealsSpent} Seals (you now hold ${r.sealsBalance}).`,
            };
            await refresh();
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'The caravan-master shakes his head.' };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Almsgiving — clickable curio names open the item-info popup.
      main.querySelectorAll<HTMLElement>('[data-item-info]').forEach((el) => {
        attachItemInfoToggle(el, el.dataset.itemInfo!);
      });
    };

    anchored(renderMain);
  });
}

function renderTaskRow(q: QuestDto): string {
  const cls = q.claimedAt ? 'claimed' : q.claimable ? 'claimable' : '';
  const pct = q.target > 0 ? Math.max(0, Math.min(100, (q.progress / q.target) * 100)) : 0;
  const cap = Math.min(q.progress, q.target);
  return `
    <div class="task-row ${cls}">
      <div class="task-info">
        <div class="task-head">
          <div class="task-name">${escapeHtml(q.name)}</div>
          ${q.claimedAt ? '<span class="task-status claimed">✓ Claimed</span>' : q.claimable ? '<span class="task-status ready">Ready to claim</span>' : ''}
        </div>
        <p class="task-blurb">${escapeHtml(q.blurb)}</p>
        <div class="task-progress-row">
          <div class="task-progress-bar"><span class="task-progress-fill" style="width: ${pct.toFixed(1)}%;"></span></div>
          <span class="task-progress-text">${cap.toLocaleString()} / ${q.target.toLocaleString()}</span>
        </div>
      </div>
      <div class="task-side">
        <div class="task-reward">
          <span class="crowns">+${formatCrowns(q.rewardCrowns)} crowns</span>
          ${q.rewardBoons > 0 ? `<span>+${q.rewardBoons} boon${q.rewardBoons === 1 ? '' : 's'}</span>` : ''}
          ${q.rewardXp > 0 ? `<span>+${formatBigNum(q.rewardXp)} XP</span>` : ''}
        </div>
        <div class="task-cta">
          ${
            q.claimedAt
              ? ''
              : q.claimable
                ? `<button class="btn gold" data-claim="${q.slug}">Claim</button>`
                : `<button class="btn" disabled>In progress</button>`
          }
        </div>
      </div>
    </div>
  `;
}

function renderDonationRow(d: DonationDto): string {
  const cls = d.fulfilled ? 'claimed' : d.fulfillable ? 'claimable' : '';
  const pct = d.qty > 0 ? Math.max(0, Math.min(100, (d.held / d.qty) * 100)) : 0;
  const capHeld = Math.min(d.held, d.qty);
  const shortBy = Math.max(0, d.qty - d.held);
  return `
    <div class="task-row ${cls}">
      <div class="task-info">
        <div class="task-head">
          <div class="task-name">
            Donate ${d.qty.toLocaleString()} ×
            <span class="alms-item tier-${escapeHtml(d.tier)}" data-item-info="${escapeHtml(d.itemSlug)}" title="Click for full info">${escapeHtml(d.itemName)}</span>
          </div>
          ${d.fulfilled ? '<span class="task-status claimed">✓ Given</span>' : d.fulfillable ? '<span class="task-status ready">Ready to give</span>' : ''}
        </div>
        <p class="task-blurb">${escapeHtml(d.blurb)}</p>
        <div class="task-progress-row">
          <div class="task-progress-bar"><span class="task-progress-fill" style="width: ${pct.toFixed(1)}%;"></span></div>
          <span class="task-progress-text">${capHeld.toLocaleString()} / ${d.qty.toLocaleString()} held</span>
        </div>
        ${!d.fulfilled && shortBy > 0
          ? `<p class="alms-short">You need ${shortBy.toLocaleString()} more.</p>`
          : ''}
      </div>
      <div class="task-side">
        <div class="task-reward">
          <span class="crowns">+${formatCrowns(d.rewardCrowns)} crowns</span>
          ${d.rewardBoons > 0 ? `<span>+${d.rewardBoons} boon${d.rewardBoons === 1 ? '' : 's'}</span>` : ''}
          ${d.rewardXp > 0 ? `<span>+${formatBigNum(d.rewardXp)} XP</span>` : ''}
        </div>
        <div class="task-cta">
          ${
            d.fulfilled
              ? ''
              : d.fulfillable
                ? `<button class="btn gold" data-donate="${escapeHtml(d.itemSlug)}">Donate</button>`
                : `<button class="btn" disabled>Need more</button>`
          }
        </div>
      </div>
    </div>
  `;
}

function renderCommissionRow(c: CaravanCommissionDto): string {
  const cls = c.fulfilled ? 'claimed' : c.fulfillable ? 'claimable' : '';
  const pct = c.qty > 0 ? Math.max(0, Math.min(100, (c.held / c.qty) * 100)) : 0;
  const capHeld = Math.min(c.held, c.qty);
  const shortBy = Math.max(0, c.qty - c.held);
  return `
    <div class="task-row ${cls}">
      <div class="task-info">
        <div class="task-head">
          <div class="task-name">
            <span class="caravan-station">${escapeHtml(c.station)}</span>
            ${c.qty.toLocaleString()} ×
            <span class="alms-item tier-${escapeHtml(c.tier)}" data-item-info="${escapeHtml(c.itemSlug)}" title="Click for full info">${escapeHtml(c.itemName)}</span>
          </div>
          ${c.fulfilled ? '<span class="task-status claimed">✓ Delivered</span>' : c.fulfillable ? '<span class="task-status ready">Ready to deliver</span>' : ''}
        </div>
        <div class="task-progress-row">
          <div class="task-progress-bar"><span class="task-progress-fill" style="width: ${pct.toFixed(1)}%;"></span></div>
          <span class="task-progress-text">${capHeld.toLocaleString()} / ${c.qty.toLocaleString()} held</span>
        </div>
        ${!c.fulfilled && shortBy > 0
          ? `<p class="alms-short">Craft ${shortBy.toLocaleString()} more at ${escapeHtml(c.station)}.</p>`
          : ''}
      </div>
      <div class="task-side">
        <div class="task-reward">
          <span class="crowns">+${c.rewardSeals.toLocaleString()} ◈ Seals</span>
        </div>
        <div class="task-cta">
          ${
            c.fulfilled
              ? ''
              : c.fulfillable
                ? `<button class="btn gold" data-commission="${escapeHtml(c.family)}">Deliver</button>`
                : `<button class="btn" disabled>Need more</button>`
          }
        </div>
      </div>
    </div>
  `;
}

function renderWare(w: CaravanWareDto, seals: number): string {
  const disabled = w.owned || w.atDailyLimit || seals < w.costSeals;
  const limitNote = w.dailyLimit > 0
    ? `<span class="caravan-ware-limit">${w.dailyLimit === 1 ? 'once a day' : `${w.dailyLimit}/day`}</span>`
    : '';
  let cta: string;
  if (w.owned) cta = '<span class="caravan-ware-owned">Owned</span>';
  else if (w.atDailyLimit) cta = '<span class="caravan-ware-owned">Back tomorrow</span>';
  else {
    const label = seals < w.costSeals ? 'Need more' : 'Buy';
    cta = `<button class="btn ${disabled ? '' : 'gold'}" data-ware="${escapeHtml(w.slug)}" ${disabled ? 'disabled' : ''}>${label}</button>`;
  }
  const nameHtml = w.itemSlug
    ? `<span class="alms-item" data-item-info="${escapeHtml(w.itemSlug)}" title="Click for full info">${escapeHtml(w.name)}</span>`
    : escapeHtml(w.name);
  return `
    <div class="caravan-ware ${w.owned ? 'owned' : ''}">
      <div class="caravan-ware-name">${nameHtml} ${limitNote}</div>
      <p class="caravan-ware-blurb">${escapeHtml(w.blurb)}</p>
      <div class="caravan-ware-foot">
        <span class="caravan-ware-cost">◈ ${w.costSeals.toLocaleString()}</span>
        ${cta}
      </div>
    </div>
  `;
}

function timeUntilUtcMidnight(): string {
  const now = new Date();
  const tomorrow = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0, 0, 0,
  ));
  const secs = Math.max(0, Math.floor((tomorrow.getTime() - now.getTime()) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${m}m`;
}

const STYLES = `
  .daily-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--s-4);
    margin-bottom: var(--s-4);
  }
  .daily-summary-figure {
    background: rgba(var(--c-surface-tint-strong), 0.55);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    padding: var(--s-3);
  }
  .daily-summary-figure .label {
    font-family: var(--font-display);
    font-size: 0.95rem;
    color: var(--c-ink-soft);
    letter-spacing: 0.03em;
  }
  .daily-summary-figure .value {
    font-family: var(--font-numeric);
    font-weight: 700;
    font-size: 1.4rem;
    color: var(--c-ink);
    margin: 4px 0;
  }
  .daily-summary-figure .value.gold { color: var(--c-gold-text); }
  .daily-summary-figure .cap {
    font-family: var(--font-elegant);
    font-style: italic;
    font-size: 0.95rem;
    color: var(--c-ink-soft);
  }
  .task-list { display: flex; flex-direction: column; gap: var(--s-3); }
  .task-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--s-4);
    padding: var(--s-3) var(--s-4);
    background: rgba(var(--c-surface-tint-strong), 0.65);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    align-items: center;
  }
  .task-row.claimed {
    border-color: var(--c-success);
    background: rgba(63, 107, 44, 0.10);
  }
  .task-row.claimable {
    border-color: var(--c-gold);
    background: rgba(212, 169, 60, 0.18);
    box-shadow: 0 0 0 1px var(--c-gold);
  }
  .task-head { display: flex; align-items: baseline; gap: var(--s-2); flex-wrap: wrap; }
  .task-name {
    font-family: var(--font-display);
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--c-ink);
    letter-spacing: 0.03em;
  }
  .task-row.claimable .task-name { color: var(--c-oxblood); }
  .task-status {
    font-family: var(--font-display);
    font-size: 0.78rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 1px 8px;
    border-radius: 999px;
    border: 1px solid currentColor;
  }
  .task-status.claimed {
    color: #f3e7c8;
    background: var(--c-success);
    border-color: var(--c-success);
  }
  .task-status.ready {
    color: var(--c-on-gold);
    background:
      linear-gradient(to bottom, var(--c-gold-bright), var(--c-gold));
    border-color: var(--c-rule-strong);
    font-weight: 700;
  }
  .task-blurb {
    font-family: var(--font-elegant);
    font-style: italic;
    font-size: 1rem;
    color: var(--c-ink-soft);
    margin: 4px 0 6px 0;
  }
  .task-progress-row {
    display: flex;
    align-items: center;
    gap: var(--s-2);
    max-width: 480px;
  }
  .task-progress-bar {
    flex: 1 1 auto;
    height: 9px;
    background: rgba(28, 17, 8, 0.18);
    border: 1px solid var(--c-rule);
    border-radius: 2px;
    overflow: hidden;
    box-shadow: var(--shadow-press);
  }
  .task-progress-fill {
    display: block;
    height: 100%;
    background: linear-gradient(to bottom, #b22c2c, #6b1f1f);
    transition: width 0.25s ease;
  }
  .task-row.claimable .task-progress-fill,
  .task-row.claimed .task-progress-fill {
    background: linear-gradient(to bottom, var(--c-gold-bright), var(--c-gold));
  }
  .task-progress-text {
    font-family: var(--font-numeric);
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--c-ink);
    font-variant-numeric: tabular-nums;
    min-width: 70px;
    text-align: right;
  }
  .task-side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--s-2);
    min-width: 180px;
  }
  .task-reward {
    display: flex;
    flex-direction: column;
    gap: 3px;
    align-items: flex-end;
    font-family: var(--font-display);
    font-size: 0.95rem;
    color: var(--c-ink);
    font-weight: 600;
    letter-spacing: 0.02em;
  }
  .task-reward .crowns {
    color: var(--c-gold-text);
    font-weight: 800;
    font-size: 1.05rem;
  }
  @media (max-width: 720px) {
    .task-row {
      grid-template-columns: 1fr;
    }
    .task-side {
      align-items: stretch;
    }
    .task-reward { align-items: flex-start; }
  }
  .alms-head {
    font-family: var(--font-display);
    margin-top: var(--s-5);
    margin-bottom: var(--s-2);
    color: var(--c-ink);
  }
  .alms-item {
    color: var(--c-gold-text);
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline dotted rgba(212, 169, 60, 0.7);
    text-underline-offset: 3px;
  }
  .alms-item:hover { text-decoration-style: solid; }
  .alms-short {
    font-family: var(--font-elegant);
    font-style: italic;
    font-size: 0.92rem;
    /* oxblood is too dark on the task card in dark mode (~1.3:1); the brighter
       oxblood keeps the warning-red identity while staying legible. */
    color: var(--c-oxblood-bright);
    font-weight: 600;
    margin: 6px 0 0 0;
  }
  /* The Wandering Caravan */
  .caravan-banner {
    display: block;
    width: 100%;
    height: auto;
    border-radius: var(--radius);
    border: 1px solid var(--c-rule);
    margin-top: var(--s-5);
    margin-bottom: var(--s-2);
  }
  .caravan-seals {
    font-family: var(--font-numeric);
    font-size: 0.95rem;
    font-weight: 800;
    color: var(--c-gold-text);
    margin-left: var(--s-3);
    vertical-align: middle;
  }
  .caravan-station {
    font-family: var(--font-display);
    font-size: 0.78rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--c-ink-soft);
    margin-right: 6px;
  }
  .caravan-shop { margin-top: var(--s-3); }
  .caravan-shop-head {
    font-family: var(--font-display);
    margin: 0 0 var(--s-3) 0;
    color: var(--c-ink);
  }
  .caravan-ware-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: var(--s-3);
  }
  .caravan-ware {
    display: flex;
    flex-direction: column;
    background: rgba(var(--c-surface-tint-strong), 0.65);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    padding: var(--s-3);
  }
  .caravan-ware.owned { opacity: 0.7; border-color: var(--c-success); }
  .caravan-ware-name {
    font-family: var(--font-display);
    font-weight: 600;
    color: var(--c-ink);
    margin-bottom: 4px;
  }
  .caravan-ware-blurb {
    font-family: var(--font-elegant);
    font-style: italic;
    font-size: 0.92rem;
    color: var(--c-ink-soft);
    margin: 0 0 var(--s-2) 0;
    flex: 1 1 auto;
  }
  .caravan-ware-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--s-2);
  }
  .caravan-ware-cost {
    font-family: var(--font-numeric);
    font-weight: 800;
    color: var(--c-gold-text);
  }
  .caravan-ware-owned {
    font-family: var(--font-display);
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--c-success);
  }
  .caravan-ware-limit {
    font-family: var(--font-display);
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--c-ink-soft);
    border: 1px solid var(--c-rule);
    border-radius: 999px;
    padding: 0 6px;
    margin-left: 4px;
    white-space: nowrap;
  }
`;
